import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { format, parseISO } from 'date-fns';
import { ru } from 'date-fns/locale';
import StarRating from '../../components/StarRating/StarRating';
import { NavigationTabs } from '../../components/NavigationTabs/NavigationTabs';
import { OtherCourses } from '../../components/OtherCourses/OtherCourses';
import { BackButton } from '../../components/BackButton/BackButton';
import { LoadingIndicator } from '../../components/LoadingIndicator/LoadingIndicator';
import { fetchProductById } from '../../api/productApi';
import { ProductDetail } from '../../types/product';
import './ProductReviews.scss';

const ProductReviews: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();

    const [activeTab, setActiveTab] = useState<'products' | 'favorites' | 'account'>('products');
    const [product, setProduct] = useState<ProductDetail | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (!id) return;

        setLoading(true);
        fetchProductById(id)
            .then(data => {
                setProduct(data);
                setLoading(false);
            })
            .catch(err => {
                console.error('Ошибка загрузки:', err);
                setError('Товар не найден');
                setLoading(false);
            });
    }, [id]);

    const handleTabChange = (tab: 'products' | 'favorites' | 'account') => {
        setActiveTab(tab);
        if (tab !== 'products') {
            navigate(`/${tab}`);
        }
    };

    const pluralizeReviews = (count: number): string => {
        if (count % 10 === 1 && count % 100 !== 11) return 'отзыв';
        if ([2, 3, 4].includes(count % 10) && ![12, 13, 14].includes(count % 100)) return 'отзыва';
        return 'отзывов';
    };

    if (loading) {
        return (
            <div className="reviews-loading">
                <LoadingIndicator message="Загрузка отзывов..." />
            </div>
        );
    }

    if (error || !product) {
        return (
            <div className="reviews-not-found">
                <BackButton to={product?.id ? `/product/${product.id}` : '/'} />
                <div>{error || 'Товар не найден'}</div>
            </div>
        );
    }

    const reviews = product.reviews || [];

    return (
        <div className="reviews-page">
            <BackButton className="reviews-back-button" to={product?.id ? `/product/${product.id}` : '/'}/>

            <div className="reviews-content">
                <div className="reviews-header">
                    <div className="rating-container">
                        <svg width={24} height={24} viewBox="0 0 24 24" fill="gold" stroke="gold" strokeWidth="1.5">
                            <path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z" />
                        </svg>
                        <div className="rating-value">{product.rating ?? '0'}</div>
                    </div>
                    <div className="reviews-count">
                        {reviews.length} {pluralizeReviews(reviews.length)}
                    </div>
                </div>

                <div className="reviews-list">
                    {reviews.length > 0 ? (
                        reviews.map((review: any) => (
                            <div key={review.id} className="review-card">
                                <div className="review-rating">
                                    <StarRating rating={review.rating} />
                                </div>
                                <div className="review-header">
                                    <span className="review-author">
                                        {review.user?.firstName} {review.user?.lastName}
                                    </span>
                                    <span className="review-date">
                                        {format(parseISO(review.createdAt), 'd MMMM', { locale: ru })}
                                    </span>
                                </div>
                                <p className="review-text">{review.text}</p>
                            </div>
                        ))
                    ) : (
                        <div className="no-reviews">Отзывов пока нет</div>
                    )}
                </div>

                <div className="other-courses">
                    <OtherCourses currentProductId={product.id} />
                </div>
            </div>

            <div className="reviews-footer">
                <NavigationTabs activeTab={activeTab} onTabChange={handleTabChange} />
            </div>
        </div>
    );
};

export default ProductReviews;